import { useState, useEffect } from 'react';

import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";

import { db } from '../firebase';
import { doc, getDoc, deleteDoc, setDoc, onSnapshot } from 'firebase/firestore';

import { functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';

import { auth } from '../firebase';
import { deleteUser } from 'firebase/auth';

import { CheckCircle, CircleUserRound, LockKeyhole, Trash2, X } from 'lucide-react';

import logoBadge from '../assets/logo_badge.png';

import Lists from './Lists';
import Callbacks from './Callbacks';
import Overdues from './Overdues';
import LoadingScreen from './LoadingScreen';

const SUBSCRIBE_STATUSES = ['new', 'expired', 'cancelled'];
const PAYMENT_ERROR_STATUSES = ['suspended', 'payment_failed'];

const Hub = ({
  handleSignOut, uid, myLists,
  selectedList, setSelectedList,
  callbacks, overdues, setMyLists,
  setCallbacks, upcoming, setUpcoming,
  setOverdues, handleResetPassword
}) => {

  const [activeTab, setActiveTab] = useState('lists');
  const [showProfile, setShowProfile] = useState(false);
  const [userZoomProfile, setUserZoomProfile] = useState({});
  const [paypalSub, setPaypalSub] = useState({ subStatus: undefined });
  const [subLoading, setSubLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleDeleteList = async (listId) => {
    const permission = window.confirm('Are you sure you want to delete this list?');
    if (permission) {
      const listDocRef = doc(db, 'autodialer', 'users', 'ids', uid, 'lists', listId);
      await deleteDoc(listDocRef);
      setMyLists(prev => {
        const i = prev.findIndex(list => list.id === listId);
        const next = [...prev];
        next.splice(i, 1);
        return next;
      })
    }
  }

  const handleDeleteAccount = async () => {
    const permission = window.confirm('Are you sure you want to delete your account? You will permanently lose access to your account. Contact us within 30 days to restore your account.');
    if (permission) {
      const deleteReqDocRef = doc(db, 'autodialer', 'deletionRequests', 'ids', uid);
      await setDoc(deleteReqDocRef, {
        reqDate: new Date(),
        userEmail: userZoomProfile.email,
      });
      deleteUser(auth.currentUser);
    }
  }

  const handleCancelSubscription = async () => {
    if (!paypalSub.subId) {
      window.alert('No active PayPal subscription was found to cancel.');
      return;
    }
    const cancelPaypalSub = httpsCallable(functions, 'cancelPaypalSub');
    const confirmation = window.confirm('Are you sure you want to cancel your subscription?');
    if (confirmation) {
      setShowProfile(false);
      setLoading(true);
      cancelPaypalSub({ subId: paypalSub.subId })
        .then(() => {
          setPaypalSub(prev => ({ ...prev, subStatus: 'cancelled' }));
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
          window.alert('Could not cancel your subscription. Please try again or contact support.');
        });
    }
  }

  const createSubscription = (data, actions) => {
    console.log(data);
    return actions.subscription.create({
      plan_id: "P-591059633S527974LNDUEEZY",
      custom_id: uid
    });
  }

  const onApprove = async (data) => {
    console.log(data);
    setPaypalSub({
      subStatus: "active",
      subId: data.subscriptionID
    });
    window.alert(`Success:
      Your SmartDial subscription is now active. Your free trial ends in 14 days. Cancel before your trial ends to avoid unwanted charges.`);
  }

  useEffect(() => {
    setSubLoading(true);
    const subDocRef = doc(db, 'paypalSubscriptions', uid);
    const unsubscribe = onSnapshot(subDocRef, (subDoc) => {
      if (!subDoc.exists()) {
        setPaypalSub({ subStatus: undefined });
      } else {
        const subData = subDoc.data();
        setPaypalSub({
          subStatus: subData.subStatus,
          subId: subData.subId
        });
      }
      setSubLoading(false);
    }, (error) => {
      console.error('Error listening to subscription status:', error);
      setSubLoading(false);
    });

    const fetchUserZoomProfile = async () => {
      const userDocRef = doc(db, 'autodialer', 'users', 'ids', uid);
      const userDoc = await getDoc(userDocRef);
      setUserZoomProfile(userDoc.data().zoomInfo);
    }
    fetchUserZoomProfile();

    return () => unsubscribe();
  }, [uid]);

  const renderSubscribeButtons = () => (
    <PayPalScriptProvider options={{
      clientId: "AdgJlCXtxBCVt9nT9oJXy98nvM_eA8nNjHmTlTpSARV2fHtXoP6OR2Cz3NZfGhY_4UQGlWh5bTSdTtCf",
      vault: true,
      intent: "subscription"
    }}>
      <PayPalButtons
        style={{ color: "silver", shape: "pill", layout: "vertical", tagline: false, label: "subscribe" }}
        createSubscription={createSubscription}
        onApprove={onApprove}
      />
    </PayPalScriptProvider>
  );

  const subStatus = paypalSub.subStatus;
  const showSubscribeWall = SUBSCRIBE_STATUSES.includes(subStatus);
  const showPaymentErrorWall = PAYMENT_ERROR_STATUSES.includes(subStatus);
  const statusUnknown = !subLoading && !subStatus;

  return (
    <div className='hub'>
      {loading && <LoadingScreen text='Cancelling subscription...' />}
      {(subLoading || statusUnknown) && <LoadingScreen text='Loading subscription...' />}
      {
        showSubscribeWall &&
        <div className='lightbox'>
          <div className='lightbox-modal'>
            <div className='paywall-content'>
              <img src={logoBadge} alt='creel' width={48} />
              <br />
              <br />
              {
                subStatus === 'new' ? (
                  <>
                    <h1>Welcome to SmartDial!</h1>
                    <h3>Start your free trial now.</h3>
                    <br />
                    <p>Take full advantage of SmartDial for 2 full weeks for free.</p>
                  </>
                ) : (
                  <>
                    <h1>Resubscribe to SmartDial</h1>
                    <h3>Your subscription is no longer active.</h3>
                    <br />
                    <p>Subscribe again to continue using SmartDial.</p>
                  </>
                )
              }
              <br />
              <div>
                <div className='paywall-point'>
                  <CheckCircle style={{ marginRight: "0.8rem" }} size={"1.6rem"} color='var(--accent-1)' />
                  Reduce wrap-up time</div>
                <div className='paywall-point'>
                  <CheckCircle style={{ marginRight: "0.8rem" }} size={"1.6rem"} color='var(--accent-1)' />
                  Manage callbacks</div>
                <div className='paywall-point'>
                  <CheckCircle style={{ marginRight: "0.8rem" }} size={"1.6rem"} color='var(--accent-1)' />
                  Keep notes</div>
                <div className='paywall-point'>
                  <CheckCircle style={{ marginRight: "0.8rem" }} size={"1.6rem"} color='var(--accent-1)' />
                  Make more sales</div>
              </div>
              <br />
              <h1 style={{ color: "var(--accent-1)" }}>$9.99/user/mo</h1>
              <a href='mailto:contact@getcreel.com'>Contact sales for enterprise pricing</a>
              <br />
              <br />
              {renderSubscribeButtons()}
            </div>
          </div>
        </div>
      }
      {
        showPaymentErrorWall &&
        <div className='lightbox'>
          <div className='lightbox-modal'>
            <div className='paywall-content'>
              <img src={logoBadge} alt='creel' width={48} />
              <br />
              <br />
              <h1>Payment issue</h1>
              <h3>
                {
                  subStatus === 'payment_failed'
                    ? 'Your payment method was declined.'
                    : 'Your subscription has been suspended.'
                }
              </h3>
              <br />
              <p>
                To restore access, cancel your current subscription and subscribe again with an updated payment method.
              </p>
              <br />
              <button
                type='button'
                className='main-btn'
                onClick={handleCancelSubscription}
                disabled={!paypalSub.subId || loading}
              >
                Cancel subscription
              </button>
              <br />
              <br />
              <p style={{ color: 'var(--neutral-400)', fontSize: '1.2rem' }}>
                After you cancel, you will be prompted to subscribe again.
              </p>
            </div>
          </div>
        </div>
      }
      <div className="top-bar">
        <div className="tabs">
          <div onClick={() => setActiveTab('lists')} className={activeTab === "lists" ? "tab active" : "tab"}>Lists</div>
          <div onClick={() => setActiveTab('callbacks')} className={activeTab === "callbacks" ? "tab active" : "tab"}>Callbacks{callbacks.length > 0 && <span className='cb'>{callbacks.length}</span>}</div>
          <div onClick={() => setActiveTab('overdues')} className={activeTab === "overdues" ? "tab active" : "tab"}>Overdues{overdues.length > 0 && <span className='o'>{overdues.length}</span>}</div>
        </div>
        <div className="menu">
          <button className='menu-btn' onClick={() => setShowProfile(true)}><CircleUserRound size={'1.6rem'} /></button>
          <button className='menu-btn' onClick={() => handleSignOut()}><LockKeyhole size={'1.6rem'} /></button>
        </div>
      </div>
      {
        activeTab === 'lists' && selectedList && uid ?
          <Lists
            myLists={myLists}
            setMyLists={setMyLists}
            selectedList={selectedList}
            setSelectedList={setSelectedList}
            setCallbacks={setCallbacks}
            setUpcoming={setUpcoming}
            uid={uid}
          />
          : activeTab === 'callbacks' ?
            <Callbacks
              callbacks={callbacks}
              setCallbacks={setCallbacks}
              upcoming={upcoming}
              setUpcoming={setUpcoming}
              uid={uid}
            />
            : activeTab === 'overdues' ?
              <Overdues
                overdues={overdues}
                setOverdues={setOverdues}
                setUpcoming={setUpcoming}
                setCallbacks={setCallbacks}
                uid={uid}
              />
              : null
      }
      {
        showProfile &&
        <div className='lightbox'>
          <div className='lightbox-header'>
            Profile
            <button className='close-btn' onClick={() => setShowProfile(false)}>
              <X size={'1.2rem'} />
            </button>
          </div>
          <div className='lightbox-modal' style={{ height: '80%' }}>
            <div className='user-profile'>
              <div className='user-profile-top'>
                <div className='avatar'>
                  {userZoomProfile.display_name?.[0]}
                </div>
                <div>
                  <div className='user-name'>
                    {userZoomProfile.display_name}
                  </div>
                  <div>
                    {userZoomProfile.email}
                  </div>
                  <div className='url' onClick={() => handleResetPassword()}>Reset password</div>
                  <div className='url' onClick={() => handleDeleteAccount()}>Delete account</div>
                  <div className='url' onClick={() => handleCancelSubscription()}>Cancel subscription</div>
                </div>
              </div>
              <div className='my-lists'>
                My lists
                {myLists.map((list) => {
                  return (
                    <div className='list-item' key={list.id}>
                      {list.listName}
                      <button className='menu-btn' onClick={() => handleDeleteList(list.id)}>
                        <Trash2 size={'1.2rem'} strokeWidth={2.5} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
}

export default Hub;
